<template>
  <VideoFileStatus
    v-if="videoFileId"
    :video-file-id="videoFileId"
  />
  <div 
    v-else-if="props.channelId"
    class="video-file-select"
    @click="openPopup"
  >
    <div class="select-placeholder">
      <div class="select-icon">
        <FontAwesomeIcon icon="film" size="lg" />
      </div>
      <div class="select-text">
        Click to select a video file
      </div>
    </div>
  </div>
  <div 
    v-else
    class="video-file-select"
  >
    <div class="select-placeholder">
      <div class="select-icon">
        <FontAwesomeIcon icon="warning" size="lg" />
      </div>
      <div class="select-text">
        Select a channel before selecting a video file
      </div>
    </div>
  </div>

  <!-- Popup overlay -->
  <Teleport to="body" v-if="isPopupOpen">
    <div class="popup-overlay" @click.self="closePopup">
      <div class="popup-container">
        <div class="popup-header">
          <h3 class="popup-title">Select Video File</h3>
          <button class="popup-close" @click="closePopup">
            <FontAwesomeIcon icon="times" />
          </button>
        </div>
        
        <div class="popup-content">
          <div class="popup-header-actions">
            <div class="popup-search">
              <span class="search-icon">
                <FontAwesomeIcon icon="search" />
              </span>
              <input 
                type="text" 
                v-model="searchQuery" 
                placeholder="Search video files..." 
                @input="debouncedSearch"
              >
            </div>
            <router-link
              v-if="props.channelId"
              :to="`/channels/${props.channelId}/videofiles/new`"
              class="important-button medium"
              target="_blank"
              rel="noopener noreferrer"
            >
              <font-awesome-icon icon="plus" /> Upload
            </router-link>
          </div>
          
          <LoadingIndicator v-if="isLoading">Loading video files...</LoadingIndicator>
          <div v-else-if="filteredVideoFiles.length === 0" class="popup-no-results">
            <div class="popup-no-results-icon">
              <FontAwesomeIcon icon="file-video" />
            </div>
            <div>No video files found.</div>
          </div>
          
          <div v-else class="popup-grid">
            <div 
              v-for="videoFile in filteredVideoFiles" 
              :key="videoFile.id"
              class="selectable-card"
              :class="{ 'selected': tempSelectedId === videoFile.id }"
              @click="selectVideoFile(videoFile)"
            >
              <VideoFileStatus
                :video-file="videoFile"
                :key="videoFile.id"
              />
            </div>
          </div>
        </div>
        
        <div class="popup-footer">
          <button class="cancel-button medium" @click="closePopup">
            Cancel
          </button>
          <button 
            class="submit-button big" 
            @click="confirmSelection"
            :disabled="!tempSelectedId"
          >
            Select
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { FontAwesomeIcon } from '@/scripts/icons';
import { handleApiError } from '@/scripts/shared-utils';
import { getChannelVideoFiles, channelKeys } from '@/scripts/api/channelApi';
import VideoFileStatus from '@/components/VideoFileStatus.vue';
import LoadingIndicator from '@/components/LoadingIndicator.vue';

const props = defineProps({
  channelId: {
    type: String,
    required: true
  },
  videoFileId: {
    type: String,
    default: null
  }
});

const emit = defineEmits(['change']);

// State
const videoFileId = ref(props.videoFileId);
const tempSelectedId = ref(null);
const isPopupOpen = ref(false);
const searchQuery = ref('');
let searchTimeout = null;

const { data: videoFiles, isLoading } = useQuery({
  queryKey: channelKeys.getChannelVideoFiles(props.channelId),
  queryFn: () => getChannelVideoFiles(props.channelId),
  enabled: isPopupOpen,
  staleTime: 60000,
  placeholderData: [],
  onError: (error) => handleApiError(error, 'fetching video files')
});

// Watch for props changes
watch(() => props.videoFileId, (newValue) => {
  videoFileId.value = newValue;
});

// Computed property for filtered video files
const filteredVideoFiles = computed(() => {
  if (!searchQuery.value.trim()) {
    return videoFiles.value;
  }
  
  const query = searchQuery.value.toLowerCase();
  return videoFiles.value.filter(file => {
    return (
      (file.file_name && file.file_name.toLowerCase().includes(query)) ||
      (file.file_nickname && file.file_nickname.toLowerCase().includes(query))
    );
  });
});

const openPopup = () => {
  if (!props.channelId) {
    console.error('Channel ID is required to select video files');
    return;
  }
  
  tempSelectedId.value = videoFileId.value;
  isPopupOpen.value = true;
};

const closePopup = () => {
  isPopupOpen.value = false;
  tempSelectedId.value = null;
  searchQuery.value = '';
};

const selectVideoFile = (videoFile) => {
  tempSelectedId.value = videoFile.id;
};

const confirmSelection = () => {
  if (tempSelectedId.value) {
    videoFileId.value = tempSelectedId.value;
    emit('change', tempSelectedId.value);
    closePopup();
  }
};

const debouncedSearch = () => {
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }
  
  searchTimeout = setTimeout(() => {
    // The filteredVideoFiles computed property will handle the filtering
  }, 300);
};

// Initialize
onMounted(() => {
  if (props.videoFileId) {
    videoFileId.value = props.videoFileId;
  }
});
</script>

<style scoped>
@import '@/styles/shared-popup.css';
@import '@/styles/file-upload.css';
@import '@/styles/video-file.css';
@import '@/styles/file-select.css';
</style>
